import { Chart as ChartJS, ChartArea, ChartData, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import { useEffect, useMemo, useRef, useState } from "react"
import { DISTRACTION_TABLE } from "@blocks/rsrh/driving-distractibility-tool"
import { hexToRgba } from "@utils/hexToRGBA"
import { twMerge } from "tailwind-merge"

ChartJS.register(...registerables)

const getSegmentColor = (step: number) => {
  if (step <= 4) return "#91CA7080"
  if (step <= 10) return "#FFAF15A6"
  return "#DF281B"
}

// Fades the given hex colour (6 or 8 digit) down to the *same* colour at 0 opacity, rather than
// to "transparent" (which is transparent black, and visibly muddies the fade as it interpolates).
const createFillGradient = (ctx: CanvasRenderingContext2D, area: ChartArea, color: string) => {
  const hex = color.replace(/^#/, "")
  const baseHex = `#${hex.slice(0, 6)}`
  // const alpha = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
  const alpha = 0.3

  const gradient = ctx.createLinearGradient(0, area.top, 0, area.bottom)
  gradient.addColorStop(0, hexToRgba(baseHex, alpha))
  gradient.addColorStop(1, hexToRgba(baseHex, 0))
  return gradient
}

type DistractionChartProps = {
  /** Set to false to disable hover/click interactivity entirely (e.g. a static summary chart). */
  interactive?: boolean
  /** Shows a permanent dot on this step's point, with a tooltip pinned open on it. */
  highlightStep?: number
  /** Tooltip title shown for the pinned highlight point, e.g. "Your Average". */
  highlightLabel?: string
  /** Wraps the chart in the bordered/padded card. Set to false when embedding inside your own card. */
  showCard?: boolean
  /** Set to false to hide the y axis entirely (ticks, gridlines, and its title). */
  showYAxis?: boolean
  /** Set to false to hide the chart title. */
  showTitle?: boolean
  /** Steps-only tooltip, plus the hovered point's eyes-off-road time shown top-left of the chart. */
  largeTime?: boolean
  short?: boolean
}

export const DistractionChart = ({
  interactive = true,
  highlightStep,
  highlightLabel,
  showCard = true,
  showYAxis = true,
  showTitle = true,
  largeTime = false,
  short = false,
}: DistractionChartProps = {}) => {
  const chartRef = useRef<ChartJS<"line">>(null)
  const lastHoverIndexRef = useRef<number | null>(null)
  const [hoverPoint, setHoverPoint] = useState<{ steps: number; eyesOffS: number } | null>(null)

  const chartData: ChartData<"line"> = useMemo(
    () => ({
      labels: DISTRACTION_TABLE.map((row) => row.steps),
      datasets: [
        {
          label: "Estimated eyes-off-road time",
          data: DISTRACTION_TABLE.map((row) => row.eyesOffS),
          borderColor: "#000000",
          backgroundColor: "#000000",
          borderWidth: 2,
          fill: "start",
          segment: {
            borderColor: (ctx) => getSegmentColor(DISTRACTION_TABLE[ctx.p1DataIndex]?.steps ?? 0),
            backgroundColor: (ctx) => {
              // Chart.js's segment context prototypally inherits `chart` from the parent chart
              // context at runtime, but the type declarations don't include it — cast around that.
              const { chart } = ctx as unknown as { chart: ChartJS }
              return createFillGradient(
                chart.ctx,
                chart.chartArea,
                getSegmentColor(DISTRACTION_TABLE[ctx.p1DataIndex]?.steps ?? 0),
              )
            },
          },
          tension: 0.4,
          pointRadius: (ctx) => (DISTRACTION_TABLE[ctx.dataIndex]?.steps === highlightStep ? 5 : 0),
          pointBackgroundColor: (ctx) => getSegmentColor(DISTRACTION_TABLE[ctx.dataIndex]?.steps ?? 0),
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "#000000",
          pointHoverBorderColor: "#ffffff",
          pointHoverBorderWidth: 2,
        },
      ],
    }),
    [highlightStep],
  )

  // Force the tooltip open on the highlighted point rather than waiting for a hover event —
  // this is Chart.js's documented recipe for a permanently-pinned tooltip.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || highlightStep === undefined) return

    const dataIndex = DISTRACTION_TABLE.findIndex((row) => row.steps === highlightStep)
    if (dataIndex === -1) return

    const activeElements = [{ datasetIndex: 0, index: dataIndex }]
    chart.setActiveElements(activeElements)
    chart.tooltip?.setActiveElements(activeElements, {
      x: chart.scales.x.getPixelForValue(dataIndex),
      y: chart.scales.y.getPixelForValue(DISTRACTION_TABLE[dataIndex].eyesOffS),
    })
    chart.update()
  }, [highlightStep, chartData])

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      events: interactive ? undefined : [],

      // Chart.js calls onHover on every mousemove, not just when the active point changes — bail
      // out unless the hovered data index actually changed, otherwise every pixel of mouse movement
      // triggers a setState (and, via the new `options` object below, a full chart re-render).
      onHover: largeTime
        ? (_event, elements) => {
            const index = elements.length ? elements[0].index : null
            if (index === lastHoverIndexRef.current) return
            lastHoverIndexRef.current = index

            if (index === null) {
              setHoverPoint(null)
              return
            }
            const row = DISTRACTION_TABLE[index]
            setHoverPoint(row ? { steps: row.steps, eyesOffS: row.eyesOffS } : null)
          }
        : undefined,

      font: {
        size: 10,
      },

      interaction: {
        mode: "index",
        intersect: false,
      },

      scales: {
        x: {
          title: { display: false, text: "Steps", color: "#000000", font: { weight: "bold", size: 10 } },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: "#000000",
            font: { weight: "bold", size: 10 },
          },
        },
        y: {
          display: showYAxis,
          title: {
            display: true,
            text: "Estimated eyes-off-road time",
            color: "#000000",
            font: { weight: "bold", size: 10 },
          },
          grid: { display: true },
          border: { display: false },
          ticks: {
            color: "#8E8E8E",
            font: { size: 10 },
            callback: (value) => `${value}s`,
          },
        },
      },

      plugins: {
        title: {
          display: showTitle,
          text: "Estimated eyes-off-road time by number of steps",
          color: "#000000",
          font: { weight: "bold", size: 12 },
          align: "start",
          padding: { top: 0, bottom: 30 },
        },
        legend: { display: false },
        tooltip: {
          mode: "index",
          intersect: false,
          usePointStyle: true,
          padding: 11,
          titleFont: { size: 14 },
          bodyFont: { size: 12 },
          ...(largeTime
            ? {
                // `filter` would drop items from the title callback too (it crashes on items[0]), so
                // instead the body line is kept but rendered empty/boxless — only the title (the
                // steps) is visible, with the seconds shown top-left of the chart on hover instead.
                displayColors: false,
                bodySpacing: 0,
              }
            : {}),
          callbacks: largeTime
            ? {
                title: (items) => (highlightLabel ? highlightLabel : `${items[0].label} steps`),
                label: () => "",
              }
            : {
                title: (items) => (highlightLabel ? highlightLabel : `${items[0].label} steps`),
                label: (item) =>
                  highlightLabel ? `${highlightStep} steps` : `  ${item.formattedValue}s eyes-off-road time`,
                labelColor: (item) => {
                  const color = getSegmentColor(DISTRACTION_TABLE[item.dataIndex]?.steps ?? 0)
                  return {
                    borderColor: color,
                    backgroundColor: color,
                  }
                },
                labelPointStyle: () => ({
                  pointStyle: "circle",
                  rotation: 0,
                }),
              },
        },
      },
    }),
    [interactive, showYAxis, showTitle, highlightLabel, highlightStep, largeTime],
  )

  const chart = (
    <div className={twMerge("relative w-full", short ? "h-[200px]" : "h-[300px]")}>
      {largeTime && hoverPoint && (
        <div className="absolute top-4 left-4 flex flex-col pointer-events-none">
          <span className="type-body-s">Eyes-off road time</span>
          <span className="type-title-l">{hoverPoint.eyesOffS.toFixed(2)}s</span>
        </div>
      )}
      <Chart ref={chartRef} type="line" data={chartData} options={options} />
    </div>
  )

  if (!showCard) return chart

  return <div className="w-full p-6 my-6 border border-solid border-grey rounded-md">{chart}</div>
}
